import React, { useState } from "react";
import { toast } from "sonner";
import { PlayCircle, RefreshCw } from "lucide-react";
import { api, formatApiError, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { SafeAssignmentsPanel } from "../components/client-groups/SafeAssignmentsPanel";
import { ReviewSuggestionsPanel } from "../components/client-groups/ReviewSuggestionsPanel";
import { FirmHygienePanel } from "../components/client-groups/FirmHygienePanel";
import { ManualToolsPanel } from "../components/client-groups/ManualToolsPanel";
import { AuditLogPanel } from "../components/client-groups/AuditLogPanel";

const Kpi = ({ label, value, testid, tone }) => (
  <div className="p-4 rounded-xl border border-[#E5E7EB] bg-white" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-[#6B7280]">{label}</div>
    <div className={`text-2xl font-semibold tabular-nums mt-1 ${tone || "text-[#0A2540]"}`}>{value}</div>
  </div>
);

export default function AdminClientGrouping() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("review");
  const [rev, setRev] = useState(0);

  const runDryRun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/operations/payoffs/client-groups/backfill/dry-run");
      setPlan(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  const changed = () => { setRev((r) => r + 1); runDryRun(); };
  const t = plan?.totals;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="client-grouping-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Data quality · reconciliation</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Client grouping</h1>
          <div className="text-sm text-[#6B7280] mt-1.5 max-w-3xl">Decides which advances belong to the same plaintiff (<span className="font-mono">client_group_id</span>) for consolidated payoffs and the attorney portal. A dry run reads data only and writes nothing. Every change needs its own confirmation and is recorded in the audit log.</div>
          {plan && <div className="text-[11px] text-[#9CA3AF] mt-1 tabular-nums" data-testid="dry-run-meta">Dry run generated {formatDate(plan.generated_at)} · fingerprint {plan.plan_hash} · no writes</div>}
        </div>
        <Button onClick={runDryRun} disabled={loading} data-testid="run-dry-run-btn" className="bg-[#0A2540] hover:bg-[#13365C] text-white h-10">
          {plan ? <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
          {loading ? "Running…" : plan ? "Re-run dry run" : "Run dry run (no writes)"}
        </Button>
      </div>

      {!plan && (
        <div className="p-10 rounded-xl border border-dashed border-[#D1D5DB] bg-white text-center text-sm text-[#6B7280]" data-testid="dry-run-empty">
          Run a dry run to see the proposed assignments, suggestions to review, and firm_id issues. Nothing is written.
        </div>
      )}

      {plan && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <Kpi label="Active loans (not deleted)" value={t.non_deleted_loans} testid="kpi-non-deleted" />
            <Kpi label="Already grouped" value={t.already_grouped} testid="kpi-already-grouped" />
            <Kpi label="Safe loan writes" value={t.safe_loan_writes} testid="kpi-safe-writes" tone="text-[#059669]" />
            <Kpi label="Needs review" value={t.review_suggestions} testid="kpi-review" tone="text-[#1D4ED8]" />
            <Kpi label="Rejected" value={t.rejected_suggestions} testid="kpi-rejected" />
            <Kpi label="firm_id issues" value={plan.firm_hygiene.missing_firm_id_count + plan.firm_hygiene.mismatched_firm_id_count} testid="kpi-firm-issues" tone="text-[#B45309]" />
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="review" data-testid="tab-review">Needs review ({t.review_suggestions})</TabsTrigger>
              <TabsTrigger value="safe" data-testid="tab-safe">Safe assignments ({t.safe_loan_writes})</TabsTrigger>
              <TabsTrigger value="firm" data-testid="tab-firm">firm_id issues</TabsTrigger>
              <TabsTrigger value="manual" data-testid="tab-manual">Manual merge / split</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
            </TabsList>
            <TabsContent value="review"><ReviewSuggestionsPanel plan={plan} onChanged={changed} /></TabsContent>
            <TabsContent value="safe"><SafeAssignmentsPanel plan={plan} onChanged={changed} /></TabsContent>
            <TabsContent value="firm"><FirmHygienePanel hygiene={plan.firm_hygiene} /></TabsContent>
            <TabsContent value="manual"><ManualToolsPanel onChanged={changed} /></TabsContent>
            <TabsContent value="audit"><AuditLogPanel refreshKey={rev} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
